import * as dagre from "@dagrejs/dagre";
import { type Edge, type Node, type XYPosition } from "reactflow";
import type NodeData from "./NodeData";

/**
 * Sizes to fall back on for a node React Flow hasn't measured yet.
 *
 * A node only gets its `width` and `height` once it has been rendered and
 * observed, so a graph laid out right after loading a file can still have a few
 * unmeasured nodes. Guessing is better than treating them as zero-sized, which
 * would let their neighbors overlap them.
 */
const fallbackNodeWidth = 200;
const fallbackNodeHeight = 160;

/**
 * Gap between two nodes in the same column, in px.
 */
const nodeSeparation = 40;

/**
 * Gap between two columns, in px. Wider than the node separation so that the
 * edges have room to fan out and stay readable.
 */
const rankSeparation = 100;

/**
 * Arranges the nodes so that the graph reads left to right.
 *
 * The direction is the whole point: a computational graph flows from its inputs
 * to its outputs, which is the same direction the forward pass runs and the
 * opposite of the reverse-mode chain rule. Dagre puts every node one column to
 * the right of the nodes feeding it, so the columns end up matching the layers
 * of the topological order.
 *
 * Only the positions change. The core graph, the values and the derivatives are
 * all untouched, so laying out never affects a single result.
 *
 * @param nodes React Flow nodes.
 * @param edges React Flow edges.
 * @param snapGridSize Grid step to round the positions to, or null to place
 * them exactly where dagre put them.
 * @returns The nodes at their new positions.
 */
const layoutGraph = (
  nodes: Node[],
  edges: Edge[],
  snapGridSize: number | null,
): Node[] => {
  if (nodes.length === 0) {
    return nodes;
  }

  const graph = new dagre.graphlib.Graph();
  graph.setGraph({
    rankdir: "LR",
    nodesep: nodeSeparation,
    ranksep: rankSeparation,
  });
  // Dagre needs a default for edge labels even though no edge carries one
  graph.setDefaultEdgeLabel(() => ({}));

  const nodeIds = new Set(nodes.map((node) => node.id));
  nodes.forEach((node) => {
    graph.setNode(node.id, {
      width: getNodeWidth(node),
      height: getNodeHeight(node),
    });
  });
  edges.forEach((edge) => {
    // An edge whose ends aren't both laid out would make dagre invent nodes for
    // them, which would then push the real nodes around
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      return;
    }
    graph.setEdge(edge.source, edge.target);
  });

  dagre.layout(graph);

  const positions = new Map<string, XYPosition>();
  // Dagre centers every node of a rank on the same axis, so its own x is what
  // identifies a column. The left edges aren't: converting a center to a corner
  // subtracts each node's own width, which leaves nodes of different widths in
  // one column sitting at slightly different x
  const columnByNode = new Map<string, number>();
  nodes.forEach((node) => {
    const laidOutNode = graph.node(node.id);
    if (laidOutNode === undefined) {
      return;
    }
    // Dagre reports the center of a node, React Flow wants the top left corner
    positions.set(node.id, {
      x: laidOutNode.x - getNodeWidth(node) / 2,
      y: laidOutNode.y - getNodeHeight(node) / 2,
    });
    columnByNode.set(node.id, laidOutNode.x);
  });

  alignSourcesWithInputPorts(nodes, edges, positions, columnByNode);

  return nodes.map((node) => {
    const position = positions.get(node.id);
    if (position === undefined) {
      return node;
    }

    return {
      ...node,
      position: {
        x: snapToGrid(position.x, snapGridSize),
        y: snapToGrid(position.y, snapGridSize),
      },
    };
  });
};

/**
 * Reorders the nodes feeding one node so they follow its input ports.
 *
 * Dagre treats a node as a single point and knows nothing about which port an
 * edge lands on, so it happily places the source of the first port below the
 * source of the second one. The edges then have to cross to reach their ports,
 * which reads as if the layout rewired the graph.
 *
 * The fix stays inside one column: the nodes keep the slots dagre gave them and
 * only swap which node sits in which slot, so nothing can overlap and no other
 * column moves. Only sources feeding this one node are touched, since moving a
 * node that feeds something else as well would just trade one crossing for
 * another.
 *
 * @param nodes React Flow nodes.
 * @param edges React Flow edges.
 * @param positions Positions from dagre, updated in place.
 * @param columnByNode Which column dagre put each node in.
 */
const alignSourcesWithInputPorts = (
  nodes: Node[],
  edges: Edge[],
  positions: Map<string, XYPosition>,
  columnByNode: Map<string, number>,
): void => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));

  const outgoingEdgeCounts = new Map<string, number>();
  edges.forEach((edge) => {
    const count = outgoingEdgeCounts.get(edge.source) ?? 0;
    outgoingEdgeCounts.set(edge.source, count + 1);
  });

  nodes.forEach((targetNode) => {
    const portIds = ((targetNode.data as NodeData).inputItems ?? []).map(
      (inputItem) => inputItem.id,
    );
    // A single port can't be in the wrong order relative to itself
    if (portIds.length < 2) {
      return;
    }

    const portIndexBySource = new Map<string, number>();
    edges.forEach((edge) => {
      if (edge.target !== targetNode.id) {
        return;
      }
      if (typeof edge.targetHandle !== "string") {
        return;
      }
      const portIndex = portIds.indexOf(edge.targetHandle);
      if (portIndex < 0) {
        return;
      }
      // A source wired to several ports is pinned by its topmost one
      const knownIndex = portIndexBySource.get(edge.source);
      if (knownIndex === undefined || portIndex < knownIndex) {
        portIndexBySource.set(edge.source, portIndex);
      }
    });

    const movableSourceIds = Array.from(portIndexBySource.keys()).filter(
      (sourceId) =>
        outgoingEdgeCounts.get(sourceId) === 1 &&
        positions.has(sourceId) &&
        nodesById.has(sourceId),
    );

    groupByColumn(movableSourceIds, columnByNode).forEach((columnSourceIds) => {
      if (columnSourceIds.length < 2) {
        return;
      }
      reorderColumn(columnSourceIds, portIndexBySource, positions, nodesById);
    });
  });
};

/**
 * Groups node IDs by the column they were placed in.
 * @param nodeIds Node IDs to group.
 * @param columnByNode Which column dagre put each node in.
 * @returns One entry per column.
 */
const groupByColumn = (
  nodeIds: string[],
  columnByNode: Map<string, number>,
): string[][] => {
  const columns = new Map<number, string[]>();
  nodeIds.forEach((nodeId) => {
    const columnX = columnByNode.get(nodeId);
    if (columnX === undefined) {
      return;
    }
    const column = columns.get(columnX) ?? [];
    column.push(nodeId);
    columns.set(columnX, column);
  });
  return Array.from(columns.values());
};

/**
 * Rearranges one column so its nodes follow the port order.
 *
 * The vertical gaps are read off the current arrangement and reused, so the
 * column ends up spanning exactly what it did before and two nodes can never
 * land on top of each other, however different their heights are.
 *
 * @param nodeIds Node IDs sharing a column.
 * @param portIndexBySource Which port each of them feeds.
 * @param positions Positions from dagre, updated in place.
 * @param nodesById Nodes by ID, for their heights.
 */
const reorderColumn = (
  nodeIds: string[],
  portIndexBySource: Map<string, number>,
  positions: Map<string, XYPosition>,
  nodesById: Map<string, Node>,
): void => {
  const getY = (nodeId: string): number => positions.get(nodeId)?.y ?? 0;
  const getHeight = (nodeId: string): number => {
    const node = nodesById.get(nodeId);
    return node === undefined ? fallbackNodeHeight : getNodeHeight(node);
  };

  const byPosition = [...nodeIds].sort((a, b) => getY(a) - getY(b));
  const gaps: number[] = [];
  for (let i = 0; i < byPosition.length - 1; i += 1) {
    gaps.push(
      getY(byPosition[i + 1]) -
        (getY(byPosition[i]) + getHeight(byPosition[i])),
    );
  }

  const byPort = [...nodeIds].sort((a, b) => {
    const portDifference =
      (portIndexBySource.get(a) ?? 0) - (portIndexBySource.get(b) ?? 0);
    // Several sources on one multi-edge port are all tied, so they keep the
    // order dagre chose for them instead of being shuffled arbitrarily
    return portDifference !== 0 ? portDifference : getY(a) - getY(b);
  });

  let y = getY(byPosition[0]);
  byPort.forEach((nodeId, index) => {
    const position = positions.get(nodeId);
    if (position === undefined) {
      return;
    }
    positions.set(nodeId, { x: position.x, y });
    y += getHeight(nodeId) + (gaps[index] ?? 0);
  });
};

const getNodeWidth = (node: Node): number => node.width ?? fallbackNodeWidth;

const getNodeHeight = (node: Node): number => node.height ?? fallbackNodeHeight;

/**
 * Rounds a coordinate onto the grid.
 *
 * Snapping is normally React Flow's job while dragging, but it can't apply to
 * positions set from code, so a layout done with snapping on would leave every
 * node just off the grid it's supposed to sit on.
 *
 * @param value The coordinate.
 * @param gridSize Grid step, or null to leave the coordinate alone.
 * @returns The rounded coordinate.
 */
const snapToGrid = (value: number, gridSize: number | null): number => {
  if (gridSize === null || gridSize <= 0) {
    return value;
  }
  return Math.round(value / gridSize) * gridSize;
};

export { layoutGraph };
